import type { LifecycleStatus, PaymentStatus, TaskPriority, TaskStatus, UserRole } from './types';

export const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export function formatMoney(cents: number): string {
  return currencyFormatter.format(cents / 100);
}

export function formatCentsForInput(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(cents / 100);
}

export function parseBrlToCents(value: string): number | null {
  const compact = value.trim().replace(/R\$/gi, '').replace(/\s/g, '');
  if (!compact) return null;
  let normalized = compact;
  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  }
  if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
  const [whole = '', fraction = ''] = normalized.split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
}

export function formatDate(value: string | Date): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function clinicDateKey(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function calculateAge(birthDate: string, now = new Date()): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!match) return null;

  const [, year, month, day] = match;
  const birthDateUtc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (birthDateUtc.toISOString().slice(0, 10) !== birthDate) return null;

  const currentDate = clinicDateKey(now);
  if (birthDate > currentDate) return null;

  const [currentYear = 0, currentMonth = 0, currentDay = 0] = currentDate.split('-').map(Number);
  let age = currentYear - Number(year);
  if (
    currentMonth < Number(month) ||
    (currentMonth === Number(month) && currentDay < Number(day))
  ) {
    age -= 1;
  }
  return age;
}

export function clinicMonthIndex(value: string | Date): number {
  const date = typeof value === 'string' ? new Date(value) : value;
  const month = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    month: 'numeric',
  }).format(date);
  return Number(month) - 1;
}

export function formatTime(value: string | Date): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export function formatDateTime(value: string | Date): string {
  if (!value) return '—';
  return `${formatDate(value)} às ${formatTime(value)}`;
}

export function formatShortDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  })
    .format(date)
    .replace('.', '');
}

export function formatRelativeDate(value: string, now = new Date()): string {
  const target = new Date(value);
  const difference = target.getTime() - now.getTime();
  const days = Math.round(difference / 86_400_000);
  if (days === 0) return `Hoje, ${formatTime(target)}`;
  if (days === 1) return `Amanhã, ${formatTime(target)}`;
  if (days === -1) return `Ontem, ${formatTime(target)}`;
  return formatDateTime(target);
}

export const lifecycleLabels: Record<LifecycleStatus, string> = {
  pending_scheduling_confirmation: 'Aguardando confirmação de agendamento',
  scheduled: 'Agendamento confirmado — aguardando confirmação da consulta',
  confirmed: 'Consulta confirmada',
  completed: 'Realizada',
  no_show: 'Paciente faltou',
  rescheduled: 'Reagendada',
  cancelled: 'Cancelada',
};

export const paymentLabels: Record<PaymentStatus, string> = {
  pending: 'Pendente',
  partially_paid: 'Parcialmente paga',
  paid: 'Paga',
  partially_refunded: 'Parcialmente devolvida',
  refunded: 'Devolvida',
};

export const taskStatusLabels: Record<TaskStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

export const taskPriorityLabels: Record<TaskPriority, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

export const roleLabels: Record<UserRole, string> = {
  medico: 'Médico',
  secretaria: 'Secretária',
};

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function maskCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function maskPostalCode(value: string): string {
  return onlyDigits(value)
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
}

export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
}

export function toWhatsAppUrl(phone: string): string {
  const digits = onlyDigits(phone);
  const national = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${national}`;
}

export function toDateTimeLocal(value: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

export function clinicDateToEndOfDayIso(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error('Informe uma data válida.');
  const [, year, month, day] = match;
  const localDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (localDate.toISOString().slice(0, 10) !== value) {
    throw new Error('Informe uma data válida.');
  }
  const nextLocalDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day) + 1))
    .toISOString()
    .slice(0, 10);
  const nextDayStart = new Date(clinicDateTimeLocalToIso(`${nextLocalDate}T00:00`));
  return new Date(nextDayStart.getTime() - 1).toISOString();
}

function clinicOffsetMilliseconds(value: Date): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value ?? 0);
  const representedAsUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );
  return representedAsUtc - Math.floor(value.getTime() / 1000) * 1000;
}

export function clinicDateTimeLocalToIso(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error('Informe uma data e um horário válidos.');
  const [, year, month, day, hour, minute] = match;
  const wallClockUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );
  let instant = new Date(wallClockUtc);
  for (let iteration = 0; iteration < 2; iteration += 1) {
    instant = new Date(wallClockUtc - clinicOffsetMilliseconds(instant));
  }
  if (toDateTimeLocal(instant) !== value) {
    throw new Error('Este horário não existe no fuso de São Paulo. Escolha outro horário.');
  }
  return instant.toISOString();
}
